// Shows the current notice (FR-033; data-model §12): a polite live region (web role="status",
// Android accessibilityLiveRegion), dismissed after 6 s or by its labelled 44 px close button.
import { useEffect, useSyncExternalStore } from "react";
import { StyleSheet, View } from "react-native";

import { makeStyles } from "@/theme/makeStyles";
import { PressableTarget } from "@/ui/PressableTarget";
import { Text } from "@/ui/Text";

import { appNotices, type NoticeStore } from "./store";

export const NOTICE_MS = 6_000;

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    wrap: {
      position: "absolute",
      left: t.space.lg,
      right: t.space.lg,
      bottom: t.space.lg,
      alignItems: "center",
      zIndex: 20,
    },
    notice: {
      flexDirection: "row",
      alignItems: "center",
      gap: t.space.md,
      maxWidth: 480,
      paddingLeft: t.space.lg,
      backgroundColor: t.color.infoSoft,
      borderColor: t.color.border,
      borderWidth: t.borderWidth.card,
      borderRadius: t.radius.panel,
      boxShadow: t.shadow.card,
    },
    text: { flexShrink: 1, color: t.color.info },
    close: { paddingHorizontal: t.space.md },
  }),
);

export function NoticeHost({ store = appNotices }: { store?: NoticeStore }) {
  const styles = useStyles();
  const notice = useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getSnapshot,
  );

  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => store.dismiss(notice.id), NOTICE_MS);
    return () => clearTimeout(timer);
  }, [notice, store]);

  if (!notice) return null;
  return (
    <View style={styles.wrap} pointerEvents="box-none">
      <View
        style={styles.notice}
        role="status"
        accessibilityLiveRegion="polite"
        testID="notice"
      >
        <Text style={styles.text}>{notice.text}</Text>
        <PressableTarget
          accessibilityRole="button"
          accessibilityLabel="Dismiss"
          onPress={() => store.dismiss(notice.id)}
          style={styles.close}
        >
          <Text weight="semiBold" style={styles.text}>
            ×
          </Text>
        </PressableTarget>
      </View>
    </View>
  );
}
